<template>
<b-container fluid>
  <h1>FedEx Margin Report</h1>
  <b-row align-h="center">
    <b-col cols="*">
      <b-row align-h="between" class="mb-2">
        <b-btn class="mx-1" small variant="secondary" @click="setDate('thismonth')">This Month</b-btn>
        <b-btn class="mx-1" small variant="secondary" @click="setDate('lastmonth')">Last Month</b-btn>
        <b-btn class="mx-1" small variant="secondary" @click="setDate('onemonth')">One Month</b-btn>
      </b-row>
      <b-row class="my-2">
        <b-form-input type="date" v-model="form.start_date" class="m-1" />
        <b-form-input type="date" v-model="form.end_date" class="m-1 " />
      </b-row>
    </b-col>
  </b-row>
  <b-row align-h="center" class="mb-1">
    <b-col cols="*">
      <b-row align-h="between" class="my-2">
        <b-btn class="mx-1" small variant="secondary" @click="form.order_filter = 'E%'">WorkArea orders</b-btn>
        <b-btn class="mx-1" small variant="secondary" @click="form.order_filter = 'G%'">PITSCO orders</b-btn>
        <b-btn class="mx-1" small variant="secondary" @click="form.order_filter = 'Y%'">Shopify orders</b-btn>
      </b-row>
      <b-row class="my-2">
        <b-form-input placeholder="Order Filter" v-model="form.order_filter" class="mx-1" @keydown.enter="searchOrders" />
      </b-row>
    </b-col>
  </b-row>
  <b-row align-h="center" class="my-1">
    <label for="form.filter_errors">Filter for Margin Exceptions?</label>
    <b-form-checkbox v-model="form.filter_problems" class="mx-1" />
    <label for="form.filter_errors">Show Viewed?</label>
    <b-form-checkbox v-model="form.show_viewed" class="mx-1" />
  </b-row>
  <b-row align-h="center" class="mt-1">
    <b-btn variant="primary" @click="searchOrders" class="mx-1">Search Orders</b-btn>
    <b-btn variant="warning" class="mx-1" :href="csvPath">Generate CSV</b-btn>
  </b-row>
  <hr>
  <b-alert show v-if="!!alert" :variant=alertVariant>{{ alert }}</b-alert>
  <b-row align-h=center v-if="loading" class="my-1">
    <fingerprint-spinner
      :animation-duration="1500"
      :size="300"
      color="#293a93"
      class="my-3"
    />
  </b-row>
  <b-row align-h=center class="my-1" v-else-if="!loading && allOrders.length > 0">
    <b-col>
      <b-row class='justify-content-center'>
        <b-col cols=4>
          <section v-if="selectedCount > 0">
            {{ selectedCount }} row{{ selectedCount !== 1 ? 's' : '' }} selected<br/>
            <div v-if="accountingAccess">
              <i class="fas fa-fw fa-user-lock"></i>
              <b-btn variant="success" class="mb-4" @click="markViewed">Mark Row{{ selectedCount !== 1 ? 's' : '' }}  Viewed</b-btn><br/>
            </div>
            <div v-else>
              Marking off items as "Viewed" is restricted. If you believe you should have access to this functionality, ask IT for access!
            </div>
            <b-btn variant="secondary" class="mt-4" @click="deselectRows">Deselect Rows</b-btn><br/>
          </section>
        </b-col>
        <b-col cols=4>
          Shipping Cost: {{ formatMoney(totals.cost, 2) }}<br/>
          Shipping Revenue: {{ formatMoney(totals.revenue, 2) }}<br/>
          Gross Shipping Profit: {{ formatMoney(totals.profit, 2) }}<br/>
          Gross Shipping Profit Margin: {{ betterRound(totals.margin) }}%<br/><br/>
          <span>Out of {{ allOrders.length }} orders</span><br/>
          <span v-if="ignored.length > 0">
            (<strong style="color:red">{{ ignored.length }}</strong> order(s) were not shown because they were not found in the FedEx table. They were logged to the console)
          </span><br/>
          <span v-if="noShipping.length > 0">
            (<strong style="color:red">{{ noShipping.length }}</strong> order(s) had no shipping price in Fishbowl. They were logged to the console)
          </span><br/>
          <span v-if="hadExceptions.length > 0">
            (<strong style="color:red">{{ hadExceptions.length }}</strong> order(s) were not shown because there were errors. <strong style="color:red">This shouldn't happen. Contact IT.</strong>)
          </span>
        </b-col>
      </b-row>
      <b-row v-if="orders.length > 0">
        <b-col>
          <span>What's with all the red?</span>
          <b-button size="sm" variant="link" class="px-0" v-b-modal.modal>
            <i class="fas fa-question-circle"></i>
          </b-button>
          <b-modal id='modal' show centered scrollable hide-footer size="lg" title="Lots of Reasons!">
            <ul>
              <li><strong>Shipping Margin:</strong>We had a negative margin on shipping for this order</li>
              <li><strong>FedEx Sum Weight:</strong>FedEx's sum of weight of boxes was >10% more than what we said (or they didn't include data for this)</li>
              <li><strong>FedEx Sum Volume:</strong>FedEx's sum of volume of boxes was >10% more than what we said (or they didn't include data for this)</li>
              <li><strong>FedEx Calculated Dim Weight:</strong>FedEx didn't include box dimensions or a divisor for this order</li>
              <li><strong>Rated Weight:</strong>FedEx's rated weight is greater both than the weight we said AND the dimensional weight we said (or FedEx didn't include a rated weight for this order)</li>
            </ul>
          </b-modal>
        </b-col>
      </b-row>
      <b-row v-if="orders.length > 0">
        <b-col>
          <b-table :fields="fields" :items="orders" :sort-compare-options="{numeric:true}" class="fixed-table" small bordered>
            <template slot="selection" slot-scope="data">
              <b-form-checkbox v-if="!data.item.viewed" @input="updateSelected" v-model="data.item.selected" />
            </template>
            <template slot="num" slot-scope="data">
              <router-link :to="'/sales-order/view/' + data.item.num" target="_blank">
                {{ data.item.num }}
              </router-link>
            </template>
            <template slot="dateIssued" slot-scope="data">
              <NiceDate :value="data.item.dateIssued" :showTime="false" />
            </template>
            <template slot="lastShipped" slot-scope="data">
              <NiceDate :value="data.item.lastShipped" :showTime="false" />
            </template>
            <template slot="last_invoiced" slot-scope="data">
              <NiceDate :value="data.item.last_invoiced" :showTime="false" />
            </template>
            <template slot="totalPrice" slot-scope="data">
              {{ formatMoney(data.item.totalPrice) }}
            </template>
            <template slot="subtotal" slot-scope="data">
              {{ formatMoney(data.item.subtotal) }}
            </template>
            <template slot="shipping" slot-scope="data">
              {{ formatMoney(data.item.shipping) }}
            </template>
            <template slot="amego_netcharge" slot-scope="data">
              {{ formatMoney(data.item.amego_netcharge) }}
            </template>
            <template slot="am_shipping_cost" slot-scope="data">
              {{ formatMoney(data.item.am_shipping_cost) }}
            </template>
            <template slot="shippingMargin" slot-scope="data">
              <span v-if="!data.item.shippingMargin" style="color:red">
                ---
              </span>
              <span v-else-if="data.item.shippingMargin < 0" style="color:red">
                {{ betterRound(data.item.shippingMargin) }} %
              </span>
              <span v-else>
                {{ betterRound(data.item.shippingMargin) }} %
              </span>
            </template>
            <template slot="amego_weight" slot-scope="data">
              {{ betterRound(data.item.amego_weight) }} {{ data.item.weight_unit }}
            </template>
            <template slot="fedex_sum_weight" slot-scope="data">
              <span v-if="!data.item.fedex_sum_weight && data.item.fedex_sum_weight != 0" style="color:red">
                ---
              </span>
              <span v-else-if="data.item.fedex_sum_weight >= data.item.amego_weight * 1.10" style="color:red">
                {{ betterRound(data.item.fedex_sum_weight) }} {{ data.item.weight_unit }}
              </span>
              <span v-else>
                {{ betterRound(data.item.fedex_sum_weight) }} {{ data.item.weight_unit }}
              </span>
            </template>
            <template slot="calculated_dim_weight" slot-scope="data">
              <span v-if="!data.item.calculated_dim_weight && data.item.calculated_dim_weight != 0" style="color:red">
                ---
              </span>
              <span v-else>
                {{ betterRound(data.item.calculated_dim_weight) }} {{ data.item.weight_unit }}
              </span>
            </template>
            <template slot="rated_weight" slot-scope="data">
              <span v-if="!data.item.rated_weight && data.item.rated_weight != 0" style="color:red">
                ---
              </span>
              <span v-else-if="betterRound(data.item.rated_weight-data.item.amego_weight)>0 && betterRound(data.item.rated_weight-data.item.amego_volume/139)>0" style="color:red">
                {{ betterRound(data.item.rated_weight) }} {{ data.item.weight_unit }}
              </span>
              <span v-else>
                {{ betterRound(data.item.rated_weight) }} {{ data.item.weight_unit }}
              </span>
            </template>
            <template slot="amego_volume" slot-scope="data">
              {{ betterRound(data.item.amego_volume) }} {{ data.item.dim_unit }}<sup>3</sup>
            </template>
            <template slot="fedex_sum_volume" slot-scope="data">
              <span v-if="data.item.fedex_sum_volume >= data.item.amego_volume * 1.10" style="color:red">
                {{ betterRound(data.item.fedex_sum_volume) }} {{ data.item.dim_unit }}<sup>3</sup>
              </span>
              <span v-else>
                {{ betterRound(data.item.fedex_sum_volume) }} {{ data.item.dim_unit }}<sup>3</sup>
              </span>
            </template>
          </b-table>
        </b-col>
      </b-row>
    </b-col>
  </b-row>
</b-container>
</template>

<script>
import axios from 'axios';
import moment from 'moment'
import { mapGetters } from 'vuex';

import NiceDate from '../utility/NiceDate';
import utility from '../../helpers/utility';
import { FingerprintSpinner } from 'epic-spinners';
const BASE_API = process.env.VUE_API_ROOT;

export default {
  components: {
    NiceDate,
    FingerprintSpinner,
  },
  data() {
    return {
      form: {
        start_date: '2024-04-01',
        end_date: '2024-04-10',
        order_filter: 'Y%',
        filter_problems: false,
        show_viewed: false,
      },
      range: {
        min: -100,
        max: 100
      },
      margin: 0,
      loading: false,
      alert: null,
      alertVariant: 'warning',

      fields: [
        { key: 'selection', label: '', sortable: true },
        { key: 'num', sortable: true },
        { key: 'number_of_pieces', label: '# Boxes', sortable: true },
        { key: 'carrierName', sortable: true },
        { key: 'dateIssued', sortable: true },
        { key: 'lastShipped', sortable: true },
        { key: 'last_invoiced', sortable: true },
        { key: 'totalPrice', label: 'Order Total Price', sortable: true },
        { key: 'subtotal', label: 'Order Subtotal', sortable: true },
        { key: 'amego_netcharge', label: 'AMEGO Estimated Price', sortable: true },
        { key: 'shipping', label: 'Order Shipping Price', sortable: true },
        { key: 'am_shipping_cost', label: 'AM Shipping Cost', sortable: true },
        { key: 'shippingMargin', sortable: true },
        { key: 'amego_weight', label: 'AMEGO Weight', sortable: true },
        { key: 'fedex_sum_weight', sortable: true },
        { key: 'calculated_dim_weight', sortable: true },
        { key: 'rated_weight', sortable: true },
        { key: 'amego_volume', label: 'AMEGO Volume', sortable: true },
        { key: 'fedex_sum_volume', sortable: true },
      ],
      orders: [],
      ignored: [],
      noShipping: [],
      hadExceptions: [],
      totals: {
        cost: 0,
        revenue: 0,
        profit: 0,
        margin: 0,
      },
      selectedCount: 0,
    }
  },
  mounted() {
    this.setDate('lastmonth');
  },
  methods: {
    setDate(range) {
      switch(range) {
        case "thismonth":
          this.form.start_date = this.getDateString(moment().startOf('M'))
          this.form.end_date = this.getDateString(moment().add(1, 'd'))
          break
        case "lastmonth":
          this.form.start_date = this.getDateString(moment().subtract(1, 'M').startOf('M'))
          this.form.end_date = this.getDateString(moment().startOf('M'))
          break
        case "onemonth":
          this.form.start_date = this.getDateString(moment().subtract(1, 'M'))
          this.form.end_date = this.getDateString(moment().add(1, 'd'))
          break
      }
    },
    getDateString(date) {
      return date.format("YYYY-MM-DD")
    },
    searchOrders(event) {
      if (event) event.preventDefault();

      this.orders = [];
      this.ignored = [];
      this.noShipping = [];
      this.loading = true;
      this.alert = null;
      this.alertVariant = 'warning';
      this.selectedCount = 0

      axios.get(`${BASE_API}/fishbowl/reports/fedex-cost?${encodeURI(this.params)}`).then((response) => {
        if (!response.data || response.data.table.length == 0) {
          this.alert = "No records found for this search!"
        }

        this.totals = response.data.totals
        this.orders = response.data.table
        this.orders.forEach(x => {
          x.selected = false
        })
        this.ignored = response.data.ignored
        this.noShipping = response.data.noShipping
        this.hadExceptions = response.data.hadExceptions

        if (this.ignored.length > 0) console.warn("Orders not shown:", this.ignored)
        if (this.noShipping.length > 0) console.warn("No Shipping in Fishbowl:", this.noShipping)
        if (this.hadExceptions.length > 0) console.error("Had Exceptions!!:", this.hadExceptions)
      }).catch((error) => {
        console.error(error)
      }).finally(() => {
        this.loading = false;
      })
    },
    formatMoney(dollars) {
      if (!dollars) return '---';
      return utility.formatMoney(dollars);
    },
    betterRound(n, l = 0) {
      return utility.betterRound(n, l);
    },
    updateSelected(newVal) {
      if (newVal) {
        this.selectedCount += 1
      } else {
        this.selectedCount -= 1
      }
    },
    deselectRows(event) {
      if (!!event) event.preventDefault()
      for (let i = 0; i < this.orders.length; i++) {
        let x = this.orders[i]
        x.selected = false
        this.$set(this.orders, i, x)
      }
    },
    markGood(event) {
      if (!!event) event.preventDefault()
      if (!this.accountingAccess) return;

      let toMark = this.orders.filter((o) => !!o.selected)
      let tracknums = toMark.map((o) => o.mastertracknums).join(',').split(',')

      this.alertVariant = 'warning'
      this.alert = `Marking ${toMark.length} rows as viewed`

      axios({
        url: `${BASE_API}/fishbowl/reports/fedex-cost/mark-viewed`,
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        data: {
          mastertracknums: tracknums,
          user: this.loggedInUser.emails.value.split('@')[0],
        }
      }).then(response => {
        console.log(response)
        this.alertVariant = 'success'
        this.alert = `Marked ${toMark.length} rows as viewed`

        toMark.forEach(order => {
          order.viewed = true
          order.selected = false
        })
        this.selectedCount = 0
      }).catch(error => {
        console.log(error)
        this.alertVariant = 'danger'
        this.alert = 'Failed to mark orders as viewed. Nothing has been changed.'
      })
    }
  },
  computed: {
    params() {
      return Object.keys(this.form).map((k) => `${k}=${this.form[k]}`).join('&');
    },
    csvPath() {
      return `${BASE_API}/fishbowl/reports/fedex-cost.csv?${encodeURI(this.params)}&generate_csv=true`
    },
    ...mapGetters({
      canAccess: 'canAccess',
      loggedInUser: 'loggedInUser'
    }),
    accountingAccess() {
      return this.$store.getters.canAccess('accounting-access')
    },
    allOrders() {
      return this.orders.concat(this.ignored, this.noShipping, this.hadExceptions)
    },
  }
}
</script>



// WEBPACK FOOTER //
// src/components/ops-info/FedExMargins.vue